import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const MAX_UPLOAD_SIZE = 200 * 1024 * 1024; // 200 MB

// Monotonically increasing counter — gives each concurrent upload a unique ID
// so their placeholders don't collide when multiple files are pasted at once.
let _uploadSeq = 0;
function nextUploadSeq() { return ++_uploadSeq; }

/**
 * Upload a file (image or video) to S3 via the attachments API
 * @returns {Promise<string|null>} The direct URL of the uploaded file
 */
async function uploadMediaFile(file, entityType, entityId, onProgress) {
  // Client-side size guard
  if (file.size > MAX_UPLOAD_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const limitMB = MAX_UPLOAD_SIZE / (1024 * 1024);
    throw new Error(`File "${file.name}" is ${sizeMB} MB and exceeds the ${limitMB} MB upload limit.`);
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(
    `/attachments/upload?entity_type=${entityType}&entity_id=${entityId}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      timeout: 600000, // 10 min for large video uploads
      onUploadProgress: onProgress
        ? (progressEvent) => {
            const percent = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            onProgress({ loaded: progressEvent.loaded, total: progressEvent.total, percent });
          }
        : undefined,
    }
  ).catch((err) => {
    // Translate 413 into a user-friendly message
    if (err.response?.status === 413) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      throw new Error(`File "${file.name}" (${sizeMB} MB) was rejected by the server as too large. Please ask your administrator to verify the nginx client_max_body_size is set to 200M and reload nginx.`);
    }
    // Surface backend error detail if present
    const detail = err.response?.data?.detail;
    if (detail) {
      throw new Error(detail);
    }
    throw err;
  });

  // Use the stable backend proxy URL so inline images/videos never expire.
  // The /inline endpoint redirects to a fresh presigned S3 URL on each request.
  const att = response.data.attachment;
  if (att?.id) {
    return `/api/v1/attachments/${att.id}/inline`;
  }
  return att?.direct_url || att?.download_url || null;
}

// Backward-compatible alias
const uploadImageFile = uploadMediaFile;

/**
 * TipTap extension for handling image uploads (paste and toolbar)
 *
 * Options:
 *   - entityType: string (e.g. 'task')
 *   - entityId: number
 */
const ImageUploadExtension = Extension.create({
  name: 'imageUpload',

  addOptions() {
    return {
      entityType: null,
      entityId: null,
    };
  },

  addStorage() {
    return {
      onProgress: null,
      // Track concurrent uploads so the progress bar only clears when
      // all concurrent uploads finish, not when the first one completes.
      activeUploads: 0,
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { entityType, entityId } = this.options;

    return [
      new Plugin({
        key: new PluginKey('imageUpload'),
        props: {
          handlePaste(view, event) {
            if (!entityType || !entityId) return false;

            const items = event.clipboardData?.items;
            if (!items) return false;

            // Collect ALL media files from the clipboard — do NOT stop at the first one.
            const mediaFiles = [];
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.type.startsWith('image/') || item.type.startsWith('video/')) {
                const file = item.getAsFile();
                if (file) mediaFiles.push({ file, type: item.type });
              }
            }

            if (mediaFiles.length === 0) return false;

            event.preventDefault();
            mediaFiles.forEach(({ file, type }) => {
              const seq = nextUploadSeq();
              if (type.startsWith('image/')) {
                handleImageUpload(editor, file, entityType, entityId, seq);
              } else {
                handleVideoUpload(editor, file, entityType, entityId, seq);
              }
            });
            return true;
          },

          handleDrop(view, event) {
            if (!entityType || !entityId) return false;

            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return false;

            // Process ALL dropped media files, preserving their order.
            const mediaFiles = Array.from(files).filter(
              f => f.type.startsWith('image/') || f.type.startsWith('video/')
            );

            if (mediaFiles.length === 0) return false;

            event.preventDefault();
            mediaFiles.forEach(file => {
              const seq = nextUploadSeq();
              if (file.type.startsWith('image/')) {
                handleImageUpload(editor, file, entityType, entityId, seq);
              } else {
                handleVideoUpload(editor, file, entityType, entityId, seq);
              }
            });
            return true;
          },
        },
      }),
    ];
  },
});

/**
 * Notify upload progress and manage the active-upload counter.
 * The progress bar only clears after ALL concurrent uploads finish.
 */
function _notifyStart(editor, progressState) {
  const storage = editor.storage.imageUpload;
  if (!storage) return;
  storage.activeUploads = (storage.activeUploads || 0) + 1;
  if (storage.onProgress) storage.onProgress(progressState);
}

function _notifyUpdate(editor, progressState) {
  const storage = editor.storage.imageUpload;
  if (storage?.onProgress) storage.onProgress(progressState);
}

function _notifyDone(editor) {
  const storage = editor.storage.imageUpload;
  if (!storage) return;
  storage.activeUploads = Math.max(0, (storage.activeUploads || 1) - 1);
  if (storage.activeUploads === 0 && storage.onProgress) {
    storage.onProgress(null);
  }
}

function _notifyError(editor, errorState) {
  const storage = editor.storage.imageUpload;
  if (!storage) return;
  storage.activeUploads = Math.max(0, (storage.activeUploads || 1) - 1);
  if (storage.onProgress) {
    storage.onProgress(errorState);
    setTimeout(() => {
      if (storage.activeUploads === 0 && storage.onProgress) {
        storage.onProgress(null);
      }
    }, 3000);
  }
}

/**
 * Find the absolute document position of a placeholder string.
 * Returns the position where the placeholder text STARTS (not the node start),
 * so that deleteRange correctly targets the right substring even when multiple
 * concurrent placeholders are merged into a single text node.
 */
function _findPlaceholder(editor, placeholderText) {
  const { doc } = editor.state;
  let pos = null;
  doc.descendants((node, nodePos) => {
    if (pos !== null) return false;
    if (node.isText && node.text && node.text.includes(placeholderText)) {
      // nodePos is the start of the text node; add the in-node offset so we
      // target the exact substring, not the beginning of a merged text node.
      pos = nodePos + node.text.indexOf(placeholderText);
      return false;
    }
  });
  return pos;
}

/**
 * Upload an image and insert it into the editor.
 * uploadSeq makes the placeholder text unique so concurrent uploads
 * don't accidentally replace each other's placeholder.
 */
async function handleImageUpload(editor, file, entityType, entityId, uploadSeq = 0) {
  const fileName = file.name;
  const fileSize = file.size;

  _notifyStart(editor, { type: 'image', fileName, fileSize, percent: 0, status: 'uploading' });

  // Unique placeholder so multiple concurrent uploads don't collide
  const placeholderText = `Uploading image [${uploadSeq}]...`;
  editor.chain().focus().insertContent({
    type: 'text',
    text: placeholderText,
    marks: [{ type: 'italic' }],
  }).run();

  try {
    const imageUrl = await uploadImageFile(file, entityType, entityId, ({ percent }) => {
      _notifyUpdate(editor, { type: 'image', fileName, fileSize, percent, status: 'uploading' });
    });

    const placeholderPos = _findPlaceholder(editor, placeholderText);
    if (imageUrl) {
      if (placeholderPos !== null) {
        editor.chain()
          .focus()
          .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
          .insertContentAt(placeholderPos, { type: 'resizableImage', attrs: { src: imageUrl } })
          .run();
      } else {
        editor.chain().focus().insertContent({ type: 'resizableImage', attrs: { src: imageUrl } }).run();
      }
    } else if (placeholderPos !== null) {
      // Upload returned no URL — remove placeholder silently
      editor.chain()
        .focus()
        .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
        .run();
    }
    _notifyDone(editor);
  } catch (error) {
    console.error('Image upload error:', error);
    toast.error(error.message || 'Image upload failed.');
    _notifyError(editor, { type: 'image', fileName, fileSize, percent: 0, status: 'error' });

    const placeholderPos = _findPlaceholder(editor, placeholderText);
    if (placeholderPos !== null) {
      editor.chain()
        .focus()
        .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
        .insertContentAt(placeholderPos, {
          type: 'text',
          text: '[Image upload failed]',
          marks: [{ type: 'italic' }],
        })
        .run();
    }
  }
}

/**
 * Upload a video and insert it into the editor.
 */
async function handleVideoUpload(editor, file, entityType, entityId, uploadSeq = 0) {
  const fileName = file.name;
  const fileSize = file.size;

  _notifyStart(editor, { type: 'video', fileName, fileSize, percent: 0, status: 'uploading' });

  const placeholderText = `Uploading video [${uploadSeq}]...`;
  editor.chain().focus().insertContent({
    type: 'text',
    text: placeholderText,
    marks: [{ type: 'italic' }],
  }).run();

  try {
    const videoUrl = await uploadMediaFile(file, entityType, entityId, ({ percent }) => {
      _notifyUpdate(editor, { type: 'video', fileName, fileSize, percent, status: 'uploading' });
    });

    const placeholderPos = _findPlaceholder(editor, placeholderText);
    if (videoUrl) {
      if (placeholderPos !== null) {
        editor.chain()
          .focus()
          .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
          .insertContentAt(placeholderPos, { type: 'videoNode', attrs: { src: videoUrl } })
          .run();
      } else {
        editor.chain().focus().insertContent({ type: 'videoNode', attrs: { src: videoUrl } }).run();
      }
    } else if (placeholderPos !== null) {
      editor.chain()
        .focus()
        .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
        .run();
    }
    _notifyDone(editor);
  } catch (error) {
    console.error('Video upload error:', error);
    toast.error(error.message || 'Video upload failed.');
    _notifyError(editor, { type: 'video', fileName, fileSize, percent: 0, status: 'error' });

    const placeholderPos = _findPlaceholder(editor, placeholderText);
    if (placeholderPos !== null) {
      editor.chain()
        .focus()
        .deleteRange({ from: placeholderPos, to: placeholderPos + placeholderText.length })
        .insertContentAt(placeholderPos, {
          type: 'text',
          text: '[Video upload failed]',
          marks: [{ type: 'italic' }],
        })
        .run();
    }
  }
}

export { uploadImageFile, uploadMediaFile, handleImageUpload, handleVideoUpload };
export default ImageUploadExtension;
